import { differenceInDays, format, startOfDay } from "date-fns";
import { useMutation } from "react-query";

import { AnalyticsReportIProps } from "~/entity/Ivideon/model/schema";
import { useNotify } from "~/shared/hooks/useNotify";
import { parseForServer } from "~/widgets/Filters/FilterSummary/helper";
import { FormValues } from "~/widgets/Filters/FilterSummary/schema/types";

import { useIvideon } from "./useIvideon";

type AnalyticsType = "face" | "cars";

export interface getAnalyticsReportIProps {
  type: AnalyticsType;
  values: FormValues;
  filterMode: "day" | "week" | "month" | null;
}

export const useGetAnalyticaReport = () => {
  const { getAnalyticaReport } = useIvideon();
  const { notifyReport } = useNotify();

  const { mutate, isLoading, isError } = useMutation({
    mutationFn: ({
      apiKey,
      data,
    }: {
      apiKey: string;
      data: AnalyticsReportIProps;
    }) => getAnalyticaReport(apiKey, data),

    onSuccess: (response) => {
      const hash = response?.data?.fileHash;
      if (hash) {
        notifyReport(hash);
      }
    },
  });

  const getAnalyticsReport = ({
    type,
    values,
    filterMode,
  }: {
    type: AnalyticsType;
    values: FormValues;
    filterMode: getAnalyticsReportIProps["filterMode"];
  }) => {
    if (!values?.startDate || !values?.endDate) return;

    const { startDate, endDate } = values;

    const start = new Date(startDate);
    const end = new Date(endDate);

    const diffDays = differenceInDays(startOfDay(end), startOfDay(start));

    let period = "";
    let groupBy: "day" | "week" | "month" | "" = "";

    if (filterMode === "day") {
      period = "Today";
    } else if (filterMode === "week") {
      period = "Week";
    } else if (filterMode === "month") {
      period = "Month";
    } else if (filterMode === null) {
      groupBy = "month";

      if (diffDays < 7) {
        groupBy = "week";
      }

      if (diffDays < 1) {
        groupBy = "day";
      }
    }

    /**
     * Формирование apiKey происходит от period, есть 4 поинта на каждый тип (типов всего 2 - face и cars)
     * Они формируются выше исходя из "быстрого фильтра" в UI формы.
     * "Текущий день", "Текущая неделя", "Текущий месяц", "Ничего"
     * Если ничего не выбрано, то делается запрос без формального периода, но дефолтный энпдоинт требует в себя
     * доп. поле groupBy, с одним из этих значений. В зависимости от значения происходит детализация экселя на беке
     * Поэтому если filterMode выше равен null, то мы заполняем groupBy исходя из общего количества дней выбранного
     * периода
     */
    const apiKey = `${type}Analytics${period}Report`;
    const result = parseForServer({ ...values, groupBy });

    mutate({
      apiKey,
      data: {
        ...result,
        startDate: format(start, "yyyy-MM-dd"),
        endDate: format(end, "yyyy-MM-dd"),
      },
    });
  };

  return {
    getAnalyticsReport,
    isLoading,
    isError,
  };
};
